import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {GatewayService} from '@services/administration/gateway/gateway.service';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {NotificationService} from '@services/notification/notification.service';
import {SortingService} from '@services/administration/sorting/sorting.service';
import {UtilsService} from '@services/utils/utils.service';

import {Gateway} from '@models/adminstration/gateway/gateway';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {GatewaySignals} from '@models/adminstration/gateway/gateway-configuration/gateway-signals';

import {GATEWAY_CONFIG_DATATYPE} from '@constants/administration/gateway/gateway-constants';
import {GatewayMenuItems} from '@constants/administration/gateway/gateway-menu-items';
import {
  GatewayConfigurationRequest
} from '@models/adminstration/gateway/gateway-configuration/gateway-configuration-request';
import {utob} from '@prototypes/global.functions';
import {ELASTIC_SEARCH_TYPE} from '@constants/elastic-search/elastic-search-type.enum';

@Component({
  selector: 'scitis-admin-gateway-configuration',
  templateUrl: './gateway-configuration.component.html',
  styleUrls: ['./gateway-configuration.component.scss'],
  standalone: false
})
export class GatewayConfigurationComponent implements OnInit {

  gateway: Gateway = new Gateway();
  gatewaySignals: GatewaySignals[] = new Array<GatewaySignals>();
  isAdminRole: boolean = false;
  isSuperAdminRole: boolean = false;
  isTechAdminRole: boolean = false;
  superOrTechAdminRole = false;
  gatewayName: string = '';
  isCPLightGateway: boolean = false;
  isArgosGateway: boolean = false;
  isCPEdgeGateway: boolean = false;
  isCPOneGateway: boolean = false;
  isCPSoftGateway: boolean = false;
  isGenericGateway: boolean = false;
  show: GatewayMenuItems = GatewayMenuItems.DETAILS;
  packages: any[] = [];
  isEditable: boolean = false;
  isAdminOrHigher: boolean = false;
  isLoadingGatewayDetails: boolean = true;
  menuItems = GatewayMenuItems;
  public searchTerm: string = '';

  private CURRENT_PAGE_NAME = 'GatewayConfiguration';
  private DUPLICATED_SIGNAL_OR_ALIAS_ERROR = 400;
  private INVALID_SAMPLE_RATE = 510;
  private INVALID_NUMBER_OF_REGISTERS = 511;
  private gatewayId: string = '';

  private readonly loadingIndicatorPushMessage: string = '';
  private readonly pushSuccessMessage: string = '';
  private readonly pushErrorMessage: string = '';
  private readonly pushErrorTitle: string = '';
  private readonly saveErrorMessage: string = '';
  private readonly saveSuccessMessage: string = '';
  private readonly loadingIndicatorSaveMessage: string = '';
  private readonly duplicatedFieldsOrAliases: string = '';
  private readonly invalidModbusSampleRate: string = '';
  private readonly invalidModbusRegisters: string = '';
  private readonly errorSetConfig: string = '';
  private readonly errorGetConfig: string = '';

  constructor(private activatedRoute: ActivatedRoute,
              private gatewayService: GatewayService,
              private translate: TranslateService,
              private loadingIndicator: LoadingIndicatorService,
              private notification: NotificationService,
              private sortingService: SortingService,
              private router: Router,
              private utilsService: UtilsService) {
    this.searchTerm = this.router.currentNavigation()?.extras.state?.search;
    this.getGatewayDetailsFromUrl();
    this.setSuperOrTechAdminRole();
    this.setIsAdminOrHigher();
    this.loadingIndicatorPushMessage = this.translate.instant('Transferring configuration to Eclipse Hono');
    this.pushSuccessMessage = this.translate.instant('Configuration transferred to Eclipse Hono');
    this.pushErrorMessage = this.translate.instant('Configuration could not be pushed');
    this.pushErrorTitle = this.translate.instant('Ensure the device is configured in Iot Core');
    this.saveErrorMessage = this.translate.instant('Configuration could not be saved.');
    this.saveSuccessMessage = this.translate.instant('Configurations saved');
    this.loadingIndicatorSaveMessage = this.translate.instant('saving');
    this.duplicatedFieldsOrAliases = this.translate.instant('There are duplicated fields or aliases');
    this.invalidModbusRegisters = this.translate.instant('Invalid Modbus configuration. The number of registers is larger than 200');
    this.invalidModbusSampleRate = this.translate.instant('Invalid Modbus configuration. The sample rate value is invalid.');
    this.errorSetConfig = this.translate.instant('Error setting Configurations.');
    this.errorGetConfig = this.translate.instant('Error getting gateway details.');
  }

  get showConnectionTab(): boolean {
    return this.gateway.id !== '' && !this.isCPLightGateway;
  }

  private static isPackageUpdatable(revision: any, candidate: any) {
    return revision !== candidate;
  }

  ngOnInit() {
    this.isLoadingGatewayDetails = true;
    this.loadingIndicator.show(this.translate.instant('Loading gateway details'));
    this.activatedRoute.queryParams.subscribe(p => this.show = p.page ?? this.menuItems.DETAILS);
  }

  onMenuClick(val: string) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {page: val},
    });
  }

  onNavChange(event: any) {
    this.show = event.nextId;
    this.router.navigate([], {relativeTo: this.activatedRoute, queryParams: {page: this.show}});
  }

  pushToIoTCore() {
    if (!this.gatewaySignals || this.gatewaySignals.length < 1) {
      this.gatewaySignals = [];
    }
    this.gatewaySignals[0]['@type'] = GATEWAY_CONFIG_DATATYPE;
    this.loadingIndicator.show(this.loadingIndicatorPushMessage);
    this.gatewayService.pushToIotCore(this.gateway.id, this.gatewaySignals[0]).subscribe({
      next: () => {
        this.notification.success(this.pushSuccessMessage, this.CURRENT_PAGE_NAME, false);
        this.loadingIndicator.hide();
      }, error: error => {
        console.log(error);
        const errorMessage = this.handleModbusConfigPersistError(error, this.pushErrorMessage);
        this.notification.error(errorMessage, error, this.CURRENT_PAGE_NAME, true, this.pushErrorTitle);
        this.loadingIndicator.hide();
      }
    });
  }

  saveConfiguration(gatewaySignal: GatewaySignals) {
    this.loadingIndicator.show(this.loadingIndicatorSaveMessage);
    const gatewayConfigRequest = new GatewayConfigurationRequest();
    gatewayConfigRequest.configurationList = [gatewaySignal];
    this.gatewayService.saveSignalConfiguration(this.gateway.id, gatewayConfigRequest).subscribe({
      next: (response: any) => {
        this.gatewaySignals = response;
        this.notification.success(this.saveSuccessMessage, this.CURRENT_PAGE_NAME, false);
        this.loadingIndicator.hide();
      }, error: (error) => {
        console.log(error);
        const errorMessage = this.getErrorMessage(error);
        this.notification.error(errorMessage, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      }
    });
  }

  isIotGateway(): boolean {
    return !(!this.gateway.iotCore || !this.gateway.iotCore.registryId);
  }

  private getGatewayDetailsFromUrl(): void {
    const details = this.activatedRoute.snapshot.paramMap.get('details');
    if (details) {
      const parsed = JSON.parse(utob(details));
      this.gatewayId = parsed.id;
      this.isAdminRole = parsed.isAdminRole;
      this.isSuperAdminRole = parsed.isSuperAdminRole;
      this.isTechAdminRole = parsed.isTechAdminRole;
      this.gatewayService.getGatewayDetails(this.gatewayId).subscribe({
        next: (gateway) => {
          this.gateway = gateway;
          this.gatewayName = gateway.name;
          this.getTypeInformation(gateway);
          this.setPackageArray(gateway.packages);
          this.setIsEditable();
          this.setConfiguration(this.gatewayId);
          this.loadingIndicator.hide();
          this.isLoadingGatewayDetails = false;
          this.show = this.activatedRoute.snapshot.queryParams.page ?? this.menuItems.DETAILS;
        }, error: error => {
          console.log(error);
          const message = this.errorGetConfig;
          this.notification.error(message, error, this.CURRENT_PAGE_NAME, true);
          this.loadingIndicator.hide();
          this.isLoadingGatewayDetails = false;
        }
      });
    }
    this.loadingIndicator.hide();
  }

  private getTypeInformation(gateway: Gateway) {
    switch (gateway?.cloudplugType) {
      case GATEWAY_TYPE.CPLIGHT: {
        this.isCPLightGateway = true;
        break;
      }
      case GATEWAY_TYPE.ARGOS_GATEWAY: {
        this.isArgosGateway = true;
        break;
      }
      case GATEWAY_TYPE.CPEDGE: {
        this.isCPEdgeGateway = true;
        break;
      }
      case GATEWAY_TYPE.CPONE: {
        this.isCPOneGateway = true;
        break;
      }
      case GATEWAY_TYPE.GENERIC: {
        this.isGenericGateway = true;
        break;
      }
    }
  }

  private setPackageArray(gatewayPackages: any) {
    for (const key in gatewayPackages) {
      this.packages.push({
        name: gatewayPackages[key].name,
        revision: gatewayPackages[key].revision,
        candidate: gatewayPackages[key].candidate,
        updatable: GatewayConfigurationComponent.isPackageUpdatable(gatewayPackages[key].revision, gatewayPackages[key].candidate)
      });
    }
    this.packages.sort((a, b) => this.sortingService.sortByName(a, b));
  }

  private setIsEditable() {
    this.isEditable = (this.superOrTechAdminRole && !this.isGenericGateway && !this.isArgosGateway);
  }

  private setSuperOrTechAdminRole(): void {
    this.superOrTechAdminRole = this.isSuperAdminRole || this.isTechAdminRole;
  }

  private setIsAdminOrHigher(): void {
    this.isAdminOrHigher = this.utilsService.isAdminOrHigher(this.isAdminRole, this.isTechAdminRole, this.isSuperAdminRole);
  }

  private setConfiguration(gatewayId: string) {
    this.gatewayService.getGatewaySignals(gatewayId).subscribe({
      next: (signals) => {
        this.gatewaySignals = signals;
        this.loadingIndicator.hide();
      }, error: (error) => {
        this.notification.error(this.errorSetConfig, error, this.CURRENT_PAGE_NAME, true);
        this.loadingIndicator.hide();
      }
    });
  }

  private handleModbusConfigPersistError(error: { status: number }, message: string): string {
    if (error.status === this.INVALID_NUMBER_OF_REGISTERS) {
      message = this.invalidModbusRegisters;
    } else if (error.status === this.INVALID_SAMPLE_RATE) {
      message = this.invalidModbusSampleRate;
    }
    return message;
  }

  private getErrorMessage(error: any): string {
    let errorMessage = this.saveErrorMessage;
    errorMessage = this.handleModbusConfigPersistError(error, errorMessage);
    if (error.status === this.DUPLICATED_SIGNAL_OR_ALIAS_ERROR) {
      errorMessage += this.duplicatedFieldsOrAliases;
      if (error.data && error.data.length > 0) {
        errorMessage += ': ' + error.data[0];
      }
    }
    return errorMessage;
  }

  protected readonly ELASTIC_SEARCH_TYPE = ELASTIC_SEARCH_TYPE;
}
